import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { setActiveTab } from '../../redux/slices/appSlice';
import { track } from '../../utils/analytics';
import { shouldOfferBackup } from '../../utils/cloudBackup';
import './RestoreFoundPrompt.scss';

/**
 * "Turn on Cloud Backup" offer — shown at the moments the user just trusted
 * us with their library, never on a timer. Mounted for the whole session in
 * App.js (desktop only; renders nothing without the backup bridge).
 *
 * Triggers (each checked against backup being OFF + the durable per-owner
 * dismissal flags — decision logic is `shouldOfferBackup`):
 *  1. library_sync — the background local→cloud playlist sync finished
 *  2. dj_sync     — a DJ-tool import completed (manual sidebar or auto-sync)
 *  3. upgrade     — the tier flipped to GOAT mid-session
 *
 * Sync moments share one dismissal (`backupOfferDismissed`); the upgrade
 * moment re-offers once via its own flag (`backupUpgradeOfferDismissed`).
 * When cloud backups already exist and the fresh-machine restore prompt
 * hasn't been answered, RestoreFoundPrompt owns the moment — this stays out.
 */

// A hydrated last-session sync timestamp must not read as "the user just
// synced" — only transitions to a timestamp this recent count.
const RECENT_SYNC_MS = 2 * 60 * 1000;

const COPY = {
    library_sync: {
        title: 'Keep your library safe',
        sub: 'Your playlists just synced to MixGoat. Back up your DJ libraries too, so a crash or a lost laptop can’t take them.',
        cta: 'Set up backup',
    },
    dj_sync: {
        title: 'Keep your library safe',
        sub: 'Your DJ library just synced. Turn on Cloud Backup so your playlists, cues and library survive a crash or a lost laptop.',
        cta: 'Set up backup',
    },
    upgrade: {
        title: 'Cloud Backup comes with GOAT',
        sub: 'Your plan includes cloud backup for your DJ libraries and music files. Turn it on and stop worrying.',
        cta: 'Turn on backup',
    },
};

function BackupOfferPrompt() {
    const dispatch = useDispatch();
    const backupApi = typeof window !== 'undefined' ? window.electron?.backup : null;
    const userId = useSelector((s) => s.auth?.user?.id) || null;
    const tier = useSelector((s) => s.tier?.effectiveTier);
    const tierLoaded = useSelector((s) => !!s.tier?.isLoaded);
    const djSyncTimes = useSelector((s) => [
        s.rekordbox?.lastSyncTime, s.serato?.lastSyncTime,
        s.traktor?.lastSyncTime, s.virtualdj?.lastSyncTime,
    ], shallowEqual);

    const [offer, setOffer] = useState(null); // { trigger }
    const offerRef = useRef(null);
    offerRef.current = offer;
    const shownRef = useRef(new Set()); // 'sync' | 'upgrade' shown this session
    const probingRef = useRef(false);
    const prevDjRef = useRef(null);
    const prevTierRef = useRef(null);

    const attempt = async (trigger) => {
        if (!backupApi || !userId) return;
        if (offerRef.current) return; // one card at a time
        const kind = trigger === 'upgrade' ? 'upgrade' : 'sync';
        if (kind === 'upgrade' ? shownRef.current.has('upgrade') : shownRef.current.size > 0) return;
        if (probingRef.current) return;
        probingRef.current = true;
        try {
            const status = await backupApi.getStatus();
            if (!status || status.error || !status.signedIn) return;
            if (!shouldOfferBackup({ signedIn: true, settings: status.settings, trigger })) return;
            // Cloud backups waiting + restore prompt unanswered → the
            // RestoreFoundPrompt owns this user; offering "set up backup"
            // over "we found your backup" would fight it.
            if (!status.settings?.restorePromptDismissed) {
                const [snapRes, filesRes] = await Promise.all([
                    backupApi.listSnapshots(),
                    backupApi.listFiles(),
                ]);
                if ((snapRes?.snapshots || []).some((s) => s.storedAt) || (filesRes?.total || 0) > 0) return;
            }
            shownRef.current.add(kind);
            setOffer({ trigger });
            track('backup_offer_shown', { trigger });
        } catch {
            // Best-effort — a failed probe just means no offer.
        } finally {
            probingRef.current = false;
        }
    };
    const attemptRef = useRef(attempt);
    attemptRef.current = attempt;

    // Everything here is per-owner: reset session state on account switch.
    useEffect(() => {
        shownRef.current = new Set();
        prevTierRef.current = null;
        setOffer(null);
    }, [userId]);

    // 1. Library sync — the background cloud playlist sync just finished.
    useEffect(() => {
        const off = window.electron?.db?.playlists?.onCloudSynced?.(() => {
            attemptRef.current('library_sync');
        });
        return off || undefined;
    }, []);

    // 2. DJ-tool sync — any app's lastSyncTime moved to a just-now timestamp.
    useEffect(() => {
        const prev = prevDjRef.current;
        prevDjRef.current = djSyncTimes;
        if (!prev) return; // first observation is the baseline, not a sync
        const synced = djSyncTimes.some((t, i) => t && t !== prev[i]
            && Date.now() - Date.parse(t) < RECENT_SYNC_MS);
        if (synced) attemptRef.current('dj_sync');
    }, [djSyncTimes]);

    // 3. Upgrade — a resolved tier flipped to GOAT mid-session. Baselining on
    // the first LOADED value keeps boot hydration (free → cached goat) quiet.
    useEffect(() => {
        if (!tierLoaded) return;
        const prev = prevTierRef.current;
        prevTierRef.current = tier;
        if (prev && prev !== 'goat' && tier === 'goat') attemptRef.current('upgrade');
    }, [tier, tierLoaded]);

    // Either way out, the moment's durable flag is set — it never re-offers.
    const dismissFlag = (trigger) => {
        const patch = trigger === 'upgrade'
            ? { backupUpgradeOfferDismissed: true }
            : { backupOfferDismissed: true };
        backupApi?.setEnabled(patch).catch(() => {});
    };
    const accept = () => {
        track('backup_offer_accepted', { trigger: offer.trigger });
        dismissFlag(offer.trigger);
        setOffer(null);
        track('backup_page_opened', { from: 'offer_prompt' });
        dispatch(setActiveTab('backup'));
    };
    const dismiss = () => {
        track('backup_offer_dismissed', { trigger: offer.trigger });
        dismissFlag(offer.trigger);
        setOffer(null);
    };

    if (!backupApi || !offer) return null;
    const copy = COPY[offer.trigger] || COPY.dj_sync;

    return (
        <div className="rfp-overlay">
            <div className="rfp-card" role="dialog" aria-label={copy.title}>
                <div className="rfp-icon">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 17.6A4.5 4.5 0 0 0 17.5 9.4a6 6 0 0 0-11.6 1.6A4 4 0 0 0 6 19h11" />
                        <path d="M12 12v6M9.5 14.5L12 12l2.5 2.5" />
                    </svg>
                </div>
                <div className="rfp-title">{copy.title}</div>
                <div className="rfp-sub">{copy.sub}</div>
                <div className="rfp-actions">
                    <button type="button" className="rfp-quiet-btn" onClick={dismiss}>Not now</button>
                    <button type="button" className="rfp-primary-btn" onClick={accept}>{copy.cta}</button>
                </div>
            </div>
        </div>
    );
}

export default BackupOfferPrompt;
